import axios from 'axios';
import { signInWithPopup, GoogleAuthProvider, signInWithRedirect, getRedirectResult, signOut as firebaseJsSignOut, deleteUser as firebaseJsDeleteUser } from 'firebase/auth';
import { auth } from './firebase';
import { attachAuth } from '@/utils/authSession';
import {
  FIREBASE_LOGIN_PATH,
  firebaseLoginHeaders,
  resolveNativeProviderId,
  assertNativeAuthPluginAvailable,
  withTimeout,
  NATIVE_SIGN_IN_TIMEOUT_MS,
} from '@/utils/providerAuth';

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:9000/',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
});
attachAuth(apiClient);

function nativeCapacitor() {
  try {
    return require('@capacitor/core').Capacitor;
  } catch {
    return null;
  }
}

function loadNativeAuth() {
  assertNativeAuthPluginAvailable(nativeCapacitor());
  console.log('[BST_AUTH_JS] native auth module import begin');
  const pending = import('@capacitor-firebase/authentication');
  pending.then(() => {
    console.log('[BST_AUTH_JS] native auth module imported');
  });
  return pending;
}

async function nativeIdToken(forceRefresh = false) {
  const { FirebaseAuthentication } = await loadNativeAuth();
  const result = await FirebaseAuthentication.getIdToken({ forceRefresh });
  const token = result && result.token;
  if (!token) {
    const error = new Error('NATIVE_MISSING_ID_TOKEN');
    error.code = 'NATIVE_MISSING_ID_TOKEN';
    throw error;
  }
  return token;
}

export default {

  login(user) {
    return axios.post('/login', user)
  },

  register(user) {
    return axios.post('/register', user)
  },

  async signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();
      return { idToken, user: result.user };
    } catch (error) {
      console.error('Firebase sign-in error:', error);
      throw error;
    }
  },

  async signInWithGoogleRedirect() {
    const provider = new GoogleAuthProvider();
    await signInWithRedirect(auth, provider);
  },

  async getRedirectResult() {
    try {
      const result = await getRedirectResult(auth);
      if (result) {
        const idToken = await result.user.getIdToken();
        return { idToken, user: result.user };
      }
      return null;
    } catch (error) {
      console.error('Firebase redirect error:', error);
      throw error;
    }
  },

  async signInWithGoogleNative() {
    const { FirebaseAuthentication } = await loadNativeAuth();
    console.log('[BST_AUTH_JS] before signInWithGoogle');
    const result = await withTimeout(
      FirebaseAuthentication.signInWithGoogle({ skipNativeAuth: false }),
      NATIVE_SIGN_IN_TIMEOUT_MS,
      'NATIVE_SIGN_IN_TIMEOUT'
    );
    const idToken = await nativeIdToken(true);
    return {
      idToken,
      user: result.user,
      credential: result.credential,
      additionalUserInfo: result.additionalUserInfo,
    };
  },

  async signInWithAppleNative() {
    const { FirebaseAuthentication } = await loadNativeAuth();
    console.log('[BST_AUTH_JS] before signInWithApple');
    const result = await withTimeout(
      FirebaseAuthentication.signInWithApple({ skipNativeAuth: false }),
      NATIVE_SIGN_IN_TIMEOUT_MS,
      'NATIVE_SIGN_IN_TIMEOUT'
    );
    const idToken = await nativeIdToken(true);
    return {
      idToken,
      user: result.user,
      credential: result.credential,
      additionalUserInfo: result.additionalUserInfo,
    };
  },

  async getNativeCurrentUser() {
    const { FirebaseAuthentication } = await loadNativeAuth();
    const result = await FirebaseAuthentication.getCurrentUser();
    return result && result.user ? result.user : null;
  },

  async getNativeProviderId() {
    const user = await this.getNativeCurrentUser();
    return resolveNativeProviderId(user);
  },

  async revokeAppleAccessToken(token) {
    const { FirebaseAuthentication } = await loadNativeAuth();
    await FirebaseAuthentication.revokeAccessToken({ token });
  },

  async deleteNativeFirebaseUser() {
    const { FirebaseAuthentication } = await loadNativeAuth();
    await FirebaseAuthentication.deleteUser();
  },

  async deleteJsFirebaseUser() {
    if (auth.currentUser) {
      await firebaseJsDeleteUser(auth.currentUser);
    }
  },

  async deleteFirebaseIdentity() {
    let nativeUser = null;
    try {
      const { Capacitor } = require('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        nativeUser = await this.getNativeCurrentUser();
        if (nativeUser) {
          await this.deleteNativeFirebaseUser();
        }
      }
    } catch (error) {
      if (nativeUser) throw error;
    }
    await this.deleteJsFirebaseUser();
  },

  async signOutProviders() {
    try {
      const { Capacitor } = require('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const { FirebaseAuthentication } = await loadNativeAuth();
        await FirebaseAuthentication.signOut();
      }
    } catch (error) {
      console.warn('Native Firebase sign-out skipped:', error);
    }
    try {
      if (auth.currentUser) {
        await firebaseJsSignOut(auth);
      }
    } catch (error) {
      console.warn('Firebase JS sign-out skipped:', error);
    }
  },

  async firebaseLogin(idToken) {
    return apiClient.post(FIREBASE_LOGIN_PATH, {}, {
      headers: firebaseLoginHeaders(idToken)
    });
  },
  forgotPassword(email){
    return apiClient.post('/auth/forgotpassword', email)
  },
  forgotPasswordConfirm(request){
    return apiClient.post('/auth/forgotpassword/confirm', request)
  },
  deleteAccount({ confirmation, password }) {
    const body = { confirmation };
    if (typeof password === 'string' && password.length > 0) {
      body.password = password;
    }
    return apiClient.delete('/account', { data: body });
  }
}
